import { useEffect, useState } from "react";
import { advisoryApi, type Composition, type Portfolio, type RiskCurrent } from "../advisoryApi";
import { money, toNumber } from "../format";
import { CompositionView, type Holding } from "../charts";
import { PrefillNote, SaveBar, Seg } from "../ui";
import { fromMonth, toMonth, useSaver, useStepLoad, type StepProps } from "./common";

interface PortfolioData {
  portfolio: Portfolio;
  composition: Composition | null;
  risk: RiskCurrent | null;
}

interface Form {
  name: string;
  portfolioType: string;
  scheduleType: string;
  oneTimeAmount: string;
  oneTimeDate: string;
  recurringAmount: string;
  recurringFrequency: string;
  recurringStartDate: string;
  recurringEndDate: string;
}

const FREQUENCIES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

const MODEL_FAILURE: Record<string, string> = {
  no_risk_score: "The model portfolio is chosen from the risk score — complete the risk assessment first.",
  no_portfolio_for_score: "There is no model portfolio for this risk score. Choose a custom portfolio instead.",
  no_portfolio_for_platform: "There is no model portfolio for this score on the chosen platform.",
};

function toForm(p: Portfolio): Form {
  const s = (v: unknown) => (v === null || v === undefined ? "" : String(toNumber(v) ?? v));
  return {
    name: p.name ?? "",
    portfolioType: p.portfolioType ?? "model",
    scheduleType: p.scheduleType ?? "one_time",
    oneTimeAmount: s(p.oneTimeAmount),
    oneTimeDate: toMonth(p.oneTimeDate),
    recurringAmount: s(p.recurringAmount),
    recurringFrequency: p.recurringFrequency ?? "monthly",
    recurringStartDate: toMonth(p.recurringStartDate),
    recurringEndDate: toMonth(p.recurringEndDate),
  };
}

// Portfolio types as kind-picker cards (design.md: portfolio type choice).
const TYPE_GLYPH: Record<string, string> = {
  model:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v9l7.8 4.5"/><circle cx="12" cy="12" r="9"/></svg>',
  custom:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h10M4 12h16M4 18h7"/><circle cx="17" cy="6" r="2"/><circle cx="14" cy="18" r="2"/></svg>',
};

/**
 * Portfolio Construction (`portfolio_input`) — QP's portfolio-input-widget:
 * name, type (Model / Custom), and the investment schedule (one-time amount
 * and month, or a recurring amount with frequency and start/end month). A
 * model portfolio shows the matched model's holdings; a custom one is built on
 * the flow's Custom portfolio page.
 */
export function PortfolioStep({ sessionId, addr, steps, readOnly, onSaved, prefill, clearPrefill, currency, goTo }: StepProps) {
  const pAddr = { goalId: addr.goalId, portfolioId: addr.portfolioId };
  const load = useStepLoad<PortfolioData>(sessionId, "portfolio-input", pAddr);
  const saver = useSaver(onSaved);
  const [form, setForm] = useState<Form | null>(null);
  const [base, setBase] = useState<Form | null>(null);
  const [prefillUsed, setPrefillUsed] = useState(false);

  useEffect(() => {
    if (!load.data?.portfolio) return;
    const f = toForm(load.data.portfolio);
    setForm(f);
    setBase(f);
  }, [load.data]);

  useEffect(() => {
    if (!prefill) return;
    const p = prefill.proposal;
    setForm((f) =>
      f
        ? {
            ...f,
            scheduleType: typeof p.scheduleType === "string" ? p.scheduleType : f.scheduleType,
            oneTimeAmount: p.oneTimeAmount != null ? String(p.oneTimeAmount) : f.oneTimeAmount,
          }
        : f,
    );
    setPrefillUsed(true);
  }, [prefill]);

  if (!addr.goalId || !addr.portfolioId) return <p className="muted">This page needs a portfolio.</p>;
  if (load.loading && !load.data) return <p className="muted">Loading the portfolio…</p>;
  if (load.error) return <div className="banner-error">{load.error}</div>;
  if (!form || !base) return null;

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const dirty = JSON.stringify(form) !== JSON.stringify(base);
  const composition = load.data?.composition ?? null;
  const oneTime = form.scheduleType === "one_time";
  const amountOk = oneTime ? (toNumber(form.oneTimeAmount) ?? 0) > 0 : (toNumber(form.recurringAmount) ?? 0) > 0;
  const hasCustomPage = form.portfolioType === "custom";
  const minBuy = load.data?.portfolio.minBuyViolations ?? [];

  async function save() {
    if (!form) return;
    const body: Record<string, unknown> = {
      name: form.name.trim() || "Portfolio",
      portfolioType: form.portfolioType,
      scheduleType: form.scheduleType,
    };
    if (oneTime) {
      body.oneTimeAmount = toNumber(form.oneTimeAmount);
      body.oneTimeDate = fromMonth(form.oneTimeDate);
    } else {
      body.recurringAmount = toNumber(form.recurringAmount);
      body.recurringFrequency = form.recurringFrequency;
      body.recurringStartDate = fromMonth(form.recurringStartDate);
      body.recurringEndDate = fromMonth(form.recurringEndDate);
    }
    if (prefillUsed) body.prefillUsed = true;
    const r = await saver.run(() => advisoryApi.saveStep(sessionId, "portfolio-input", pAddr, body), { goalsChanged: true });
    if (r) {
      setBase(form);
      setPrefillUsed(false);
      clearPrefill();
      load.reload();
    }
  }

  const types = [
    { value: "model", label: "Model portfolio", hint: "Matched to the risk score" },
    { value: "custom", label: "Custom portfolio", hint: "Pick products yourself" },
  ];
  if (form.portfolioType === "dynamic") types.push({ value: "dynamic", label: "Dynamic", hint: "Generated by the engine" });

  return (
    <section className="adv-step-card">
      {prefill && <PrefillNote basis={prefill.basis} onDismiss={clearPrefill} />}

      <div className="field">
        <span>Portfolio type *</span>
        <div className="kind-picker" role="radiogroup" aria-label="Portfolio type">
          {types.map((t) => (
            <button
              key={t.value}
              type="button"
              role="radio"
              aria-checked={form.portfolioType === t.value}
              className={form.portfolioType === t.value ? "kind-option active" : "kind-option"}
              onClick={() => set("portfolioType", t.value)}
              disabled={readOnly}
            >
              <span className="glyph adv-goal-glyph" dangerouslySetInnerHTML={{ __html: TYPE_GLYPH[t.value] ?? TYPE_GLYPH.model! }} />
              {t.label}
              <span className="adv-kind-hint">{t.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <label className="field">
        <span>Portfolio name</span>
        <input value={form.name} maxLength={60} onChange={(e) => set("name", e.target.value)} disabled={readOnly} />
      </label>

      <div className="field">
        <span>Investment *</span>
        <Seg
          value={form.scheduleType}
          options={[
            { value: "one_time", label: "One-time" },
            { value: "recurring", label: "Recurring" },
          ]}
          onChange={(v) => set("scheduleType", v)}
          disabled={readOnly}
          label="Schedule"
        />
      </div>
      {oneTime ? (
        <div className="field-row">
          <label className="field">
            <span>Amount ({currency}) *</span>
            <input
              type="number"
              min={0}
              step={1000}
              value={form.oneTimeAmount}
              onChange={(e) => set("oneTimeAmount", e.target.value)}
              disabled={readOnly}
            />
          </label>
          <label className="field">
            <span>Month</span>
            <input type="month" value={form.oneTimeDate} onChange={(e) => set("oneTimeDate", e.target.value)} disabled={readOnly} />
          </label>
        </div>
      ) : (
        <>
          <div className="field-row">
            <label className="field">
              <span>Amount per period ({currency}) *</span>
              <input
                type="number"
                min={0}
                step={500}
                value={form.recurringAmount}
                onChange={(e) => set("recurringAmount", e.target.value)}
                disabled={readOnly}
              />
            </label>
            <label className="field">
              <span>Frequency</span>
              <select value={form.recurringFrequency} onChange={(e) => set("recurringFrequency", e.target.value)} disabled={readOnly}>
                {FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="field-row">
            <label className="field">
              <span>Start month</span>
              <input type="month" value={form.recurringStartDate} onChange={(e) => set("recurringStartDate", e.target.value)} disabled={readOnly} />
            </label>
            <label className="field">
              <span>End month</span>
              <input type="month" value={form.recurringEndDate} onChange={(e) => set("recurringEndDate", e.target.value)} disabled={readOnly} />
            </label>
          </div>
        </>
      )}

      {minBuy.length > 0 && (
        <p className="adv-calm-note">
          {minBuy.length === 1 ? "One product is" : `${minBuy.length} products are`} below the minimum purchase amount at this
          investment size.
        </p>
      )}

      {base.portfolioType === "model" && form.portfolioType === "model" && (
        <ModelHoldings composition={composition} currency={currency} amount={toNumber(base.oneTimeAmount)} />
      )}
      {hasCustomPage && (
        <div className="adv-subsection">
          <p className="muted">
            {base.portfolioType === "custom"
              ? "The holdings are chosen on the Custom portfolio page."
              : "Save to switch to a custom portfolio — then choose the holdings on the Custom portfolio page."}
          </p>
          {base.portfolioType === "custom" && (
            <button type="button" className="link-action" onClick={() => goTo("custom_portfolio")}>
              Go to Custom portfolio →
            </button>
          )}
        </div>
      )}

      {steps.some((s) => s.status === "open") && !dirty && !amountOk && (
        <p className="muted">Add the amount the client will invest to complete this step.</p>
      )}

      <SaveBar onSave={save} saving={saver.saving} dirty={dirty} error={saver.error} readOnly={readOnly} disabled={!amountOk} />
    </section>
  );
}

function ModelHoldings({ composition, currency, amount }: { composition: Composition | null; currency: string; amount: number | null }) {
  if (!composition) return <p className="muted">The model portfolio appears once the risk score is known.</p>;
  if (composition.modelResolutionFailure) {
    return <p className="adv-calm-note">{MODEL_FAILURE[composition.modelResolutionFailure] ?? composition.modelResolutionFailure}</p>;
  }
  const rows = composition.positions ?? [];
  if (rows.length === 0) return <p className="muted">The model portfolio has no holdings yet.</p>;
  const total = rows.reduce((a, p) => a + (toNumber(p.weight) ?? 0), 0);
  const holdings: Holding[] = rows.map((p, i) => ({
    key: p.productId ?? p.isin ?? `${p.name}:${i}`,
    name: p.name,
    isin: p.isin,
    assetClass: p.assetClassName ?? null,
    weight: toNumber(p.weight) ?? 0,
    amount: amount ? ((toNumber(p.weight) ?? 0) / 100) * amount : null,
  }));
  const table = (
      <table className="table adv-table-compact">
        <thead>
          <tr>
            <th>Fund</th>
            <th>Asset class</th>
            <th className="num">Weight</th>
            {amount ? <th className="num">Amount</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.productId ?? p.isin ?? p.name}>
              <td className="strong">
                {p.name}
                {p.isin && <div className="muted adv-isin">{p.isin}</div>}
              </td>
              <td>{p.assetClassName ?? "—"}</td>
              <td className="num">{(toNumber(p.weight) ?? 0).toFixed(0)} %</td>
              {amount ? <td className="num">{money(((toNumber(p.weight) ?? 0) / 100) * amount, currency)}</td> : null}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>Total</td>
            <td className="num">{total.toFixed(0)} %</td>
            {amount ? <td className="num">{money(amount, currency)}</td> : null}
          </tr>
        </tfoot>
      </table>
  );
  return (
    <div className="adv-subsection">
      <CompositionView
        title={`Recommended model${composition.modelPortfolioName ? ` · ${composition.modelPortfolioName}` : ""}`}
        holdings={holdings}
        currency={currency}
        table={table}
      />
    </div>
  );
}
